import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, FormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import {
    CONTRACT_TERM_PRESETS,
    groupMatchesPreset,
    type ContractTermPreset,
    type ContractTermPresetState,
} from '../contract-term-presets';

@Component({
    selector: 'app-formly-contract-term-presets',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, SelectButtonModule, TooltipModule, TranslateModule],
    // Inherits into PrimeNG's togglebuttons, so only this stripe is retinted.
    styles: `
        :host {
            --p-togglebutton-background: var(--p-surface-200);
            --p-togglebutton-checked-background: var(--p-surface-200);
            --p-togglebutton-border-color: var(--p-surface-300);
            --p-togglebutton-checked-border-color: var(--p-surface-300);
            --p-togglebutton-hover-background: var(--p-surface-300);
        }

        /* The light band stays in dark mode, but the label colours don't follow it —
           dark mode would tint them light and wash them out. Pin them to the light values,
           and invert on hover/select: dark fill, white label. */
        :host-context(.dark) {
            --p-togglebutton-color: var(--p-surface-500);
            --p-togglebutton-icon-color: var(--p-surface-500);

            --p-togglebutton-hover-background: var(--p-surface-700);
            --p-togglebutton-hover-color: var(--p-surface-0);
            --p-togglebutton-icon-hover-color: var(--p-surface-0);

            --p-togglebutton-content-checked-background: var(--p-surface-700);
            --p-togglebutton-checked-color: var(--p-surface-0);
            --p-togglebutton-icon-checked-color: var(--p-surface-0);
        }
    `,
    template: `
        <p-selectButton
            [options]="presets"
            [ngModel]="selectedId"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onSelectionChange($event)"
            [allowEmpty]="true"
            [disabled]="isDisabled"
            optionValue="id"
            [attr.data-testid]="testIdBase"
        >
            <ng-template #item let-option>
                <span
                    [pTooltip]="option.tooltipKey | translate"
                    tooltipPosition="top"
                    [attr.data-testid]="testIdBase + '-' + option.id"
                >
                    {{ option.labelKey | translate }}
                </span>
            </ng-template>
        </p-selectButton>
    `,
})
export class FormlyContractTermPresetsComponent extends FieldType<FieldTypeConfig> implements OnInit {
    readonly presets: ContractTermPreset[] = CONTRACT_TERM_PRESETS;

    private readonly destroyRef = inject(DestroyRef);
    private readonly cdRef = inject(ChangeDetectorRef);

    /** Shared with the clear button, which sits in a sibling field. */
    get state(): ContractTermPresetState {
        return this.props['presetState'] as ContractTermPresetState;
    }

    get selectedId(): string | null {
        return this.state.selectedId;
    }

    get testIdBase(): string {
        return this.props['testIdBase'] as string;
    }

    get isDisabled(): boolean {
        return this.contractGroup?.disabled !== false;
    }

    private get groupKey(): string {
        return this.props['groupKey'] as string;
    }

    private get contractGroup(): AbstractControl | null {
        return this.form instanceof FormGroup ? this.form.get(this.groupKey) : null;
    }

    ngOnInit(): void {
        this.state.notifyChanged = () => this.cdRef.markForCheck();
        this.contractGroup?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.dropSelectionOnManualEdit());
    }

    onSelectionChange(id: string | null): void {
        const group = this.contractGroup;
        if (!group || group.disabled) {
            return;
        }
        this.state.selectedId = id;
        if (id == null) {
            return;
        }
        const preset = this.presets.find((entry) => entry.id === id);
        if (!preset) {
            return;
        }
        // Patched as one object so the cross-field validator never sees a half-applied term.
        group.patchValue(preset.values);
        group.markAsDirty();
        group.markAsTouched();
        group.updateValueAndValidity();
    }

    /** Value-based, so tabbing through the fields without changing anything keeps the marking. */
    private dropSelectionOnManualEdit(): void {
        const { selectedId } = this.state;
        if (selectedId == null) {
            return;
        }
        const selected = this.presets.find((entry) => entry.id === selectedId);
        if (selected && groupMatchesPreset(this.contractGroup?.value, selected.values)) {
            return;
        }
        this.state.selectedId = null;
        this.cdRef.markForCheck();
    }
}
